import { useEffect, useState } from "react";
import { api } from "../lib/api.js";

/** Mirror of GET/PUT /api/admin/app-policy (functions/api/admin/app-policy.ts). */
interface AppPolicy {
  minAppVersion: string | null;
  minVersionSetAt: string | null;
  upgradeGraceDays: number;
  upgradeDeadlineUtc: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  devicesBelowMinimum: number;
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

/**
 * "Minimum app version" card on the Download Manager page: the admin-controlled lever that makes
 * old builds update. Apps at or above the minimum are untouched; apps below it get a banner for the
 * grace period, then a lock screen with a link to the website download page. Builds before 1.0.2
 * don't understand the policy and only show as "Needs update" on Devices.
 */
export function AppPolicyCard() {
  const [policy, setPolicy] = useState<AppPolicy | null>(null);
  const [minVersion, setMinVersion] = useState("");
  const [grace, setGrace] = useState(14);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function load() {
    const res = await api.get<{ appPolicy: AppPolicy }>("/admin/app-policy");
    setPolicy(res.appPolicy);
    setMinVersion(res.appPolicy.minAppVersion ?? "");
    setGrace(res.appPolicy.upgradeGraceDays);
  }

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed to load the app policy"));
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const res = await api.put<{ appPolicy: AppPolicy }>("/admin/app-policy", {
        minAppVersion: minVersion.trim() || null,
        upgradeGraceDays: grace,
      });
      setPolicy(res.appPolicy);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  const dirty = policy !== null && (minVersion.trim() !== (policy.minAppVersion ?? "") || grace !== policy.upgradeGraceDays);

  return (
    <form className="card" onSubmit={save}>
      <h2 style={{ margin: 0 }}>Minimum app version</h2>
      <p className="muted-subtitle" style={{ margin: "4px 0 12px" }}>
        Apps below this version get {grace} day{grace === 1 ? "" : "s"} to update — they show a banner — and then lock with a
        link to the website download page. Apps at or above it are unaffected. Only builds 1.0.2 and later understand this
        setting; older builds are flagged “Needs update” on Devices but keep working until IT upgrades them.
      </p>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={{ margin: 0 }}>
          Minimum version
          <input
            value={minVersion}
            onChange={(e) => setMinVersion(e.target.value)}
            placeholder="e.g. 1.0.2"
            pattern="\d{1,5}(\.\d{1,5}){1,3}"
            title="A version like 1.0.2"
            style={{ width: 140, margin: 0 }}
          />
        </label>
        <label style={{ margin: 0 }}>
          Upgrade grace (days)
          <input
            type="number"
            min={0}
            max={365}
            value={grace}
            onChange={(e) => setGrace(Number(e.target.value))}
            style={{ width: 100, margin: 0 }}
          />
        </label>
        <button type="submit" disabled={busy || !dirty}>
          {busy ? "Saving…" : "Save"}
        </button>
        {saved && !dirty && <span className="muted-subtitle">Saved.</span>}
      </div>
      {policy && (
        <p className="muted-subtitle" style={{ margin: "12px 0 0" }}>
          {policy.minAppVersion ? (
            <>
              Minimum <strong>{policy.minAppVersion}</strong> set {fmtDate(policy.minVersionSetAt)} · devices behind it lock after{" "}
              <strong>{fmtDate(policy.upgradeDeadlineUtc)}</strong> · <strong>{policy.devicesBelowMinimum}</strong> device
              {policy.devicesBelowMinimum === 1 ? "" : "s"} currently below it.
            </>
          ) : (
            <>No minimum set — every version is accepted.</>
          )}
        </p>
      )}
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}
